<template>
  <form @submit.prevent="addCustomer">
    <div class="row">
      <!-- Customer ID -->
      <div class="mb-3">
        <label class="form-label">Customer ID</label>
        <input
          v-model="form.customerid"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.customerid }"
          readonly
        >
        <div v-if="errors.customerid" class="text-danger">
          {{ errors.customerid[0] }}
        </div>
      </div>

      <!-- Customer Name -->
      <div class="mb-3">
        <label class="form-label">Customer Name</label>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          placeholder="Enter customer name"
        >
        <div v-if="errors.name" class="text-danger">
          {{ errors.name[0] }}
        </div>
      </div>

      <!-- Phone -->
      <div class="input-blocks">
        <label class="mb-2">Phone</label>
        <input
          v-model="form.phone"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.phone }"
          placeholder="01XXXXXXXXX"
          maxlength="11"
        >
        <div v-if="errors.phone" class="text-danger">
          {{ errors.phone[0] }}
        </div>
      </div>

      <!-- Address -->
      <div class="mb-3">
        <label class="form-label">Address</label>
        <input
          v-model="form.address"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.address }"
          placeholder="Enter customer address"
        >
        <div v-if="errors.address" class="text-danger">
          {{ errors.address[0] }}
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer-btn">
      <button
        type="button"
        class="btn btn-cancel me-2"
        @click="cancelForm"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="btn btn-submit"
        :disabled="loading"
      >
        {{ loading ? 'Creating...' : 'Submit' }}
      </button>
    </div>
  </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'CustomerCreate',
  props: {
    initialCustomerId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        customerid: this.initialCustomerId,
        name: '',
        phone: '',
        address: ''
      },
      errors: {},
      loading: false
    }
  },
  methods: {
    async addCustomer() {
      this.loading = true;
      this.errors = {};

      try {
        console.log('Submitting customer form:', this.form);

        const response = await this.$http.post('/api/customers', this.form);

        // Handle success
        this.showSuccessMessage('Customer added successfully!');
        this.resetForm();

        // Emit events for parent components
        this.$emit('customer-added', response.data);

        // Dispatch custom event for modal handling
        document.dispatchEvent(new CustomEvent('customer-created', {
          detail: response.data
        }));

        // Close modal if in modal context
        this.closeModal();

      } catch (error) {
        console.error('Customer creation error:', error);

        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Error status:', error.response.status);

          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors;
          } else if (error.response.status === 401) {
            // Authentication error
            this.showErrorMessage('Authentication failed. Please refresh the page and try again.');
          } else {
            // Other errors
            this.showErrorMessage(error.response.data.message || 'An error occurred while creating the customer.');
          }
        } else {
          // Network or other errors
          this.showErrorMessage('Network error. Please check your connection and try again.');
          console.error('Network error:', error.message);
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        customerid: this.initialCustomerId,
        name: '',
        phone: '',
        address: ''
      };
      this.errors = {};
    },

    cancelForm() {
      this.resetForm();
      this.$emit('cancel');
      this.closeModal();
    },

    closeModal() {
      // If in modal, close it
      if (window.bootstrap && window.bootstrap.Modal) {
        const modal = window.bootstrap.Modal.getInstance(this.$el.closest('.modal'));
        if (modal) {
          modal.hide();
        }
      }
    },

    async generateCustomerId() {
      try {
        const response = await this.$http.get('/api/customers/generate-id');
        this.form.customerid = response.data.customerid;
      } catch (error) {
        console.error('Failed to generate customer ID:', error);
        // Fallback to timestamp-based ID
        this.form.customerid = 'CUST' + Date.now();
      }
    },

    showSuccessMessage,
    showErrorMessage
  },

  async mounted() {
    console.log('CustomerCreate component mounted');
    console.log('Initial Customer ID:', this.initialCustomerId);

    // Generate customer ID if not provided
    if (!this.form.customerid) {
      await this.generateCustomerId();
    }

    // Mark element as mounted for loading state
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.input-blocks {
  margin-bottom: 1rem;
}

.input-blocks label {
  font-weight: 600;
}
</style>
